import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { SportCategory } from "../content/siteContent";

type SportDisciplineListProps = {
  category: SportCategory;
};

export function SportDisciplineList({ category }: SportDisciplineListProps) {
  const [openIndex, setOpenIndex] = useState(0);
  const idPrefix = useId();
  const reduceMotion = useReducedMotion();

  return (
    <section className="sport-category" aria-labelledby={`${idPrefix}-heading`}>
      <header className="sport-category-header">
        <div>
          <p className="technical-label">{category.label}</p>
          <h2 id={`${idPrefix}-heading`}>{category.title}</h2>
        </div>
        <span className="sport-category-count">{category.disciplines.length} αγωνίσματα</span>
      </header>

      <div className="sport-list">
        {category.disciplines.map((discipline, index) => {
          const isOpen = index === openIndex;
          const panelId = `${idPrefix}-panel-${index}`;
          const buttonId = `${idPrefix}-button-${index}`;

          return (
            <article className={`sport-item${isOpen ? " is-open" : ""}`} key={discipline.title}>
              <h3>
                <button
                  id={buttonId}
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  data-target
                >
                  <span className="sport-item-title">
                    <span>{discipline.title}</span>
                    <small>{discipline.calibre}</small>
                  </span>
                  <ChevronDown aria-hidden="true" />
                </button>
              </h3>
              <AnimatePresence initial={false}>
                {isOpen ? (
                  <motion.div
                    id={panelId}
                    className="sport-item-panel"
                    role="region"
                    aria-labelledby={buttonId}
                    initial={reduceMotion ? false : { height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={reduceMotion ? undefined : { height: 0, opacity: 0 }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="sport-item-details">
                      <ul className="sport-item-facts" aria-label={`Βασικά στοιχεία: ${discipline.title}`}>
                        {discipline.facts.map((fact) => (
                          <li key={fact}>{fact}</li>
                        ))}
                      </ul>
                      <p>{discipline.description}</p>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </article>
          );
        })}
      </div>
    </section>
  );
}
